<template>
    <div class="access-options-panel">
        <div class="access-options-panel-block_active">
            <div class="access-options-panel-logo" :class="{'access-options-panel-logo_islogo': featureLoginLogoEnabled}">
                <div v-show="featureLoginLogoEnabled" class="logo-pic"> 
                    <publisher-icon class="logo-pic__img" :icon-name="'IconAuthorization'" />
                </div>
                <a
                    :id="btns.signIn"
                    ref="btnSignIn"
                    :data-keydown="btns.preview"
                    role="button"
                    class="btn"
                    :href="signinPageRoute"
                    @focusin="onFocusIn"
                    @focusout="onFocusOut"
                    @click.prevent="goToNextPage(signinPageRoute)"
                >
                    {{ signinText }}
                </a>
                <a
                    v-if="featureGuestModeEnabled"
                    :id="btns.preview"
                    ref="btnPreview"
                    :data-keyup="btns.signIn"
                    role="button"
                    class="btn"
                    :href="homePageRoute"
                    @focusin="onFocusIn"
                    @focusout="onFocusOut"
                    @click.prevent="goToNextPage(homePageRoute)"
                >
                    {{ accessAsGuestText }}
                </a>
            </div>
            <custom-modal
                v-if="isShownDialogModal"
                :title="dialogModal.title"
                :message="dialogModal.message"
                :buttons="dialogModal.buttons"
                :name="dialogModal.name"
                @on-btn-click="onModalClick"
            />
        </div>
    </div>
</template>

    
<script setup lang="ts">
    import { Strings } from "@/locales/types/enums/strings";
    import { linkItems } from "@/components/Header/types/LinkItemsCollection";
    import { useApplicationStore } from "@/stores/Application";
    import { useChannelsStore } from "@/stores/Channels";
    import { type ICustomModalState } from '@/composables/globalModals/types'
    import { useI18n } from "vue-i18n";
    import { ref, type Ref, onMounted, onBeforeUnmount, nextTick } from "vue";
    import { ScreenName } from "@/common/types/screen-names";

    const ApplicationStore = useApplicationStore();
    const { deleteAllSelectedIdProgram } = ApplicationStore;
    
    interface btn {
        signIn: string;
        preview: string;
    }

    const { getChannels } = useChannelsStore();
    
    const { $navigator, $router, $analytics } = useNuxtApp();
    const { t } = useI18n();
    const { featureGuestModeEnabled, featureLoginLogoEnabled, login } = useAppConfig();

    const isRegKeyListeners: Ref<boolean> = ref(false);
    const isShownDialogModal: Ref<boolean> = ref(false);
    const btnSignIn: Ref<HTMLLinkElement | null> = ref(null);
    const btns: btn = {
        signIn: "btnSignIn",
        preview: "btnPreview",
    };
    const { welcomeScreenEnabled } = login;
    const homePageRoute: string = linkItems.home.routerPath;
    const signinPageRoute: string = linkItems.logInSignIn.routerPath;
    const signinText: string = t(Strings.LOGIN_SIGNIN).toString();
    const accessAsGuestText: string = t(Strings.LOGIN_ACCESS_AS_GUEST).toString();
    const dialogModal: ICustomModalState = {
        title: t(Strings.HOME_EXIT_APP).toString(),
        message: t(Strings.HOME_CONFIRM_EXIT_APP).toString(),
        buttons: [
            t(Strings.SETTINGS_ACCOUNT_MODAL_POSITIVE).toString(),
            t(Strings.SETTINGS_ACCOUNT_MODAL_NEGATIVE).toString()
        ],
        name: "exit",
    }

    onMounted(() => {
        nextTick((): void => {
            $analytics.createScreenEvent(ScreenName.LOGIN_PAGE, ScreenName.LOGIN_PAGE);
            focusFirstOption();

            deleteAllSelectedIdProgram();
        });
    });

    onBeforeUnmount(() => {
        unregisterKeyListeners();
    });

    const onFocusIn = (): void =>{
        registerKeyListeners();
    };
    const onFocusOut = (): void => {
        unregisterKeyListeners();
    };
    const registerKeyListeners = () => { 
        if (!isRegKeyListeners.value) {
            $navigator.on("keypress:back", confirmExit);
            isRegKeyListeners.value = true;
        }
    };
    const unregisterKeyListeners = () => {
        if (isRegKeyListeners.value) {
            $navigator.off("keypress:back", confirmExit);
            isRegKeyListeners.value = false;
        }
    };
    const confirmExit = () => {
        if (welcomeScreenEnabled) {
            $router.push({ path: linkItems.logInWelcomeScreen.routerPath, query: { lastStep: '' }});
            return;
        }

        isShownDialogModal.value = true;
    }; 

    const onModalClick = async (answer: string) => {
        if (answer === t(Strings.SETTINGS_ACCOUNT_MODAL_POSITIVE)) {
            window.close();
        } else {
            isShownDialogModal.value = false;
            focusFirstOption();
        }
    };
    const focusFirstOption = () => {
        btnSignIn.value?.focus();
    };
    const  goToNextPage = (url: string) => {
        if (url === homePageRoute) {
            // load channels
            getChannels();
        }

        navigateTo(url);
    }
</script>

<style lang="scss">
    @import url("styles.scss");
</style>